import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SmsIcon from "@mui/icons-material/Sms";
import SecurityIcon from "@mui/icons-material/Security";
import * as authMod from "firebase/auth";
import { useHistory } from "react-router-dom";

import { ModularAuth } from "../../../../components/FirebaseAuth/firebase";
import UserPageLayout from "../../../../components/User/UserPageLayout";
import Loader from "../../../../components/Loader";
import { recordUserActivity } from "../../../../libs/activityLog";

const cleanAuthError = (error, fallback) => {
  const raw = String(error?.message || "").trim();
  if (!raw) return fallback;
  const withoutPrefix = raw.replace(/^Firebase:\s*/i, "");
  const withoutCode = withoutPrefix.replace(/\s*\(auth\/[^)]+\)\.?$/i, "");
  return withoutCode || fallback;
};

const verifyCardSx = {
  borderRadius: 2,
  borderColor: "#dadce0",
  overflow: "hidden",
  boxShadow: "0 18px 48px rgba(60,64,67,0.08)",
};

const verifyIconSx = {
  width: 58,
  height: 58,
  borderRadius: 2,
  bgcolor: "#e8f0fe",
  color: "#1a73e8",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const verifyButtonSx = {
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 1.5,
};

const MFAVerify = () => {
  const history = useHistory();
  const recaptchaRef = useRef(null);
  const auth = ModularAuth;
  const getActiveUser = useCallback(async () => {
    if (typeof auth.authStateReady === "function") {
      await auth.authStateReady();
    }
    return auth.currentUser;
  }, [auth]);

  const [hints, setHints] = useState([]);
  const [selectedHintUid, setSelectedHintUid] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "info", text: "" });

  useEffect(() => {
    const loadUser = async () => {
      const user = await getActiveUser();
      if (!user) {
        history.replace("/signin");
        return;
      }
      const factors = user.multiFactor?.enrolledFactors || [];
      setHints(factors);
      if (factors.length > 0) {
        setSelectedHintUid(factors[0].uid);
      }
    };
    loadUser();

    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, [getActiveUser, history]);

  const ensureRecaptcha = async () => {
    if (recaptchaRef.current) return recaptchaRef.current;

    const verifier = new authMod.RecaptchaVerifier(auth, "mfa-verify-recaptcha", {
      size: "normal",
    });
    await verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  };

  const selectedHint = hints.find((h) => h.uid === selectedHintUid);

  const sendPhoneCode = async () => {
    const user = await getActiveUser();
    if (!user || !selectedHint) return;

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      const session = await authMod.multiFactor(user).getSession();
      const verifier = await ensureRecaptcha();
      const provider = new authMod.PhoneAuthProvider(auth);
      const id = await provider.verifyPhoneNumber(
        { multiFactorHint: selectedHint, session },
        verifier
      );
      setVerificationId(id);
      setMessage({ type: "success", text: "Verification code sent." });
    } catch (error) {
      setMessage({ type: "error", text: cleanAuthError(error, "Failed to send verification code.") });
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    const user = await getActiveUser();
    if (!user || !selectedHint) return;

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      let assertion;
      if (selectedHint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
        if (!verificationId) throw new Error("Send code first.");
        const credential = authMod.PhoneAuthProvider.credential(verificationId, code);
        assertion = authMod.PhoneMultiFactorGenerator.assertion(credential);
      } else if (selectedHint.factorId === "totp" && authMod.TotpMultiFactorGenerator?.assertionForSignIn) {
        assertion = authMod.TotpMultiFactorGenerator.assertionForSignIn(selectedHint.uid, code);
      } else {
        throw new Error("Unsupported MFA factor type.");
      }

      if (typeof authMod.multiFactor(user).reauthenticateWithAssertion !== "function") {
        throw new Error("This session does not support MFA re-verification.");
      }

      await authMod.multiFactor(user).reauthenticateWithAssertion(assertion);
      await recordUserActivity("mfa verified", "User completed MFA verification");

      history.push("/user/security");
    } catch (error) {
      setMessage({ type: "error", text: cleanAuthError(error, "Verification failed.") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserPageLayout title="Security">
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2 }}>
      <Paper variant="outlined" sx={verifyCardSx}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #05690D, #1976D2)" }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2.25, md: 2.75 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={verifyIconSx}>
              <SecurityIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#30343b" }}>
                Verify multi-factor authentication
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Confirm your identity before continuing to sensitive account controls.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/user/security")}
            sx={verifyButtonSx}
          >
            Security
          </Button>
        </Stack>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {hints.length === 0 ? (
            <Alert severity="warning">No enrolled MFA factors found for your account.</Alert>
          ) : (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Verification method
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose an enrolled method, then enter the current verification code.
                </Typography>
              </Box>

              <TextField
                select
                label="MFA device"
                value={selectedHintUid}
                onChange={(e) => {
                  setSelectedHintUid(e.target.value);
                  setCode("");
                  setVerificationId("");
                }}
                fullWidth
              >
                {hints.map((hint) => (
                  <MenuItem key={hint.uid} value={hint.uid}>
                    {(hint.displayName || hint.factorId || "MFA Device")} {hint.phoneNumber ? `(${hint.phoneNumber})` : ""}
                  </MenuItem>
                ))}
              </TextField>

              {selectedHint && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#dadce0", bgcolor: "#fafafa" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ ...verifyIconSx, width: 44, height: 44, borderRadius: 1.5 }}>
                      {selectedHint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID ? (
                        <SmsIcon fontSize="small" />
                      ) : (
                        <QrCode2Icon fontSize="small" />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {selectedHint.displayName || (selectedHint.factorId === "totp" ? "Authenticator app" : "SMS verification")}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">
                          {selectedHint.phoneNumber || "Time-based one-time code"}
                        </Typography>
                        <Chip size="small" label="Enrolled" color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {selectedHint?.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID && (
                <>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#dadce0" }}>
                    <div id="mfa-verify-recaptcha" />
                  </Paper>
                  <Button
                    variant="outlined"
                    onClick={sendPhoneCode}
                    disabled={loading}
                    startIcon={loading ? <Loader size={16} /> : <SmsIcon />}
                    sx={{ ...verifyButtonSx, width: "fit-content" }}
                  >
                    Send verification code
                  </Button>
                </>
              )}

              <TextField
                label={selectedHint?.factorId === "totp" ? "Authenticator code" : "Verification code"}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                fullWidth
              />

              <Divider />
              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between">
                <Button variant="outlined" onClick={() => history.push("/user/security")} sx={verifyButtonSx}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <Loader size={16} /> : <SecurityIcon />}
                  onClick={verify}
                  disabled={loading || !code}
                  sx={verifyButtonSx}
                >
                  Verify
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Paper>
      </Box>
    </UserPageLayout>
  );
};

export default MFAVerify;
